import { useEffect, useMemo, useState } from "react";
import { useApiClient } from "./useApiClient";
import { SegmentApiClient } from "../api/apiClientSegment";
import { CherryPayApi } from "../api/models";
import { FilterMode } from "../forms/Notifications/types";
import DateUtil from "../util/DateUtil";

export type SpamWarningLevel = "soft" | "strong" | "highRisk" | null;

export interface SpamWarningData {
  level: SpamWarningLevel;
  totalCount: number;
  scheduledCount: number;
  sentCount: number;
}

const getSpamWarningLevel = (totalCount: number): SpamWarningLevel => {
  if (totalCount >= 6) return "highRisk";
  if (totalCount >= 4) return "strong";
  if (totalCount >= 3) return "soft";
  return null;
};

export const useSpamWarning = (
  businessId: string,
  filterMode: FilterMode,
  memberFilterSegment: CherryPayApi.MemberFilterSegmentIdentifier | null,
  memberFilterQuery: string
): SpamWarningData => {
  const [schedules, setSchedules] = useState<
    CherryPayApi.CherryPassNotificationSchedule[]
  >([]);
  const apiClient = useApiClient();
  const segmentApiClient = useMemo(
    () => new SegmentApiClient(apiClient.cherryPayClient),
    [apiClient]
  );

  useEffect(() => {
    const currentDateTime = DateUtil.dateToTimezoneAgnosticISO(new Date());

    const request: CherryPayApi.RecentAndUpcomingNotificationSchedulesRequest =
      {
        CurrentDateTime: currentDateTime,
        NumberOfDays: 7,
        MemberFilterSegment:
          filterMode === "segment" && memberFilterSegment !== null
            ? memberFilterSegment
            : undefined,
        MemberFilterQuery:
          filterMode === "manualFilter" ? memberFilterQuery : undefined,
      };

    const timeoutId = setTimeout(async () => {
      const result =
        await segmentApiClient.getRecentAndUpcomingNotificationSchedulesByFilter(
          businessId,
          request
        );
      if (result.ok) {
        setSchedules(result.data);
      } else {
        setSchedules([]);
      }
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [
    businessId,
    filterMode,
    memberFilterSegment?.SegmentId,
    memberFilterQuery,
    segmentApiClient,
  ]);

  return useMemo(() => {
    const scheduledCount = schedules.filter(
      (s) => s.NotificationScheduleStatus === "Scheduled"
    ).length;
    const sentCount = schedules.filter(
      (s) => s.NotificationScheduleStatus === "Sent"
    ).length;
    const totalCount = schedules.length;
    const level = getSpamWarningLevel(totalCount);

    return { level, totalCount, scheduledCount, sentCount };
  }, [schedules]);
};
